import { useState } from "react";
import { Plus } from "lucide-react";
import { addTask, useStore, allCategories } from "@/lib/store";
import { RecurrenceEndFields, endFromTask, endToFields } from "@/components/RecurrenceEndFields";
import { toast } from "sonner";

export default function AddTaskForm({ defaultDate = null, compact = false, testIdPrefix = "add", collapsible = false }) {
  const categories = useStore(allCategories);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [categoryId, setCategoryId] = useState(categories[0]?.id || "altro");
  const [date, setDate] = useState(defaultDate || "");
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState(30);
  const [recurrence, setRecurrence] = useState("none");
  const [end, setEnd] = useState(endFromTask(null));

  function submit(e) {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Scrivi cosa devi fare");
      return;
    }
    const endFields = recurrence !== "none" ? endToFields(end) : {};
    const start = defaultDate || date || null;
    if (endFields.recurrenceUntil && start && endFields.recurrenceUntil < start) {
      toast.error("L'ultimo giorno non può essere prima dell'inizio");
      return;
    }
    addTask({
      title,
      categoryId,
      date: start,
      time: time || null,
      durationMin: duration,
      recurrence,
      ...endFields
    });
    setTitle("");
    setTime("");
    setDuration(30);
    setRecurrence("none");
    setEnd(endFromTask(null));
    toast.success("Aggiunta");
  }

  return (
    <>
    {collapsible && !open && (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="md:hidden tl-paper w-full p-3 inline-flex items-center justify-center gap-2 text-sm font-medium text-[#58635B] active:scale-[0.99] transition-transform"
        data-testid={`${testIdPrefix}-task-expand`}
      >
        <Plus size={16} strokeWidth={1.75} className="text-[#8FA087]" />
        Nuova attività
      </button>
    )}
    <form onSubmit={submit} className={`tl-paper p-4 md:p-5 ${collapsible && !open ? "hidden md:block" : ""}`} data-testid={`${testIdPrefix}-task-form`}>
      <div className={`flex gap-2 ${compact ? "flex-col" : "flex-col md:flex-row md:items-stretch"}`}>
        <input
          data-testid={`${testIdPrefix}-task-title`}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Cosa devi fare?"
          className="flex-1 px-4 py-2.5 bg-[#FDFDFB] border border-[#E4E7E1] rounded-full text-sm text-[#3B443D] placeholder:text-[#6A7368] focus:outline-none focus:border-[#A3B897]"
        />
        <div className={`flex gap-2 ${compact ? "w-full" : ""}`}>
          <select
            data-testid={`${testIdPrefix}-task-category`}
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className={`px-4 py-2.5 bg-[#FDFDFB] border border-[#E4E7E1] rounded-full text-sm text-[#3B443D] focus:outline-none focus:border-[#A3B897] ${compact ? "flex-1" : "min-w-[140px]"}`}
          >
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <button
            type="submit"
            data-testid={`${testIdPrefix}-task-submit`}
            className="tl-btn-primary inline-flex items-center gap-1.5 px-5 py-2.5 bg-[#A3B897] hover:bg-[#8FA087] text-white rounded-full text-sm font-medium transition-all"
          >
            <Plus size={16} strokeWidth={1.75} />
            Aggiungi
          </button>
        </div>
      </div>
      {!compact && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {defaultDate == null && (
            <div className="min-w-0">
              <div className="tl-label mb-1">Giorno</div>
              <input
                data-testid={`${testIdPrefix}-task-date`}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full min-w-0 px-3 py-2 bg-white border border-[#E4E7E1] rounded-lg text-sm text-[#3B443D] focus:outline-none focus:border-[#A3B897]"
              />
            </div>
          )}
          <div className="min-w-0">
            <div className="tl-label mb-1">Orario</div>
            <input
              data-testid={`${testIdPrefix}-task-time`}
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full min-w-0 px-3 py-2 bg-white border border-[#E4E7E1] rounded-lg text-sm text-[#3B443D] focus:outline-none focus:border-[#A3B897]"
            />
          </div>
          <div className="min-w-0">
            <div className="tl-label mb-1">Durata (min)</div>
            <input
              data-testid={`${testIdPrefix}-task-duration`}
              type="number"
              min={5}
              step={5}
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="w-full min-w-0 px-3 py-2 bg-white border border-[#E4E7E1] rounded-lg text-sm text-[#3B443D] focus:outline-none focus:border-[#A3B897]"
            />
          </div>
          <div className="min-w-0">
            <div className="tl-label mb-1">Ricorrenza</div>
            <select
              data-testid={`${testIdPrefix}-task-recurrence`}
              value={recurrence}
              onChange={(e) => setRecurrence(e.target.value)}
              className="w-full min-w-0 px-3 py-2 bg-white border border-[#E4E7E1] rounded-lg text-sm text-[#3B443D] focus:outline-none focus:border-[#A3B897]"
            >
              <option value="none">Non ricorrente</option>
              <option value="daily">Ogni giorno</option>
              <option value="weekdays">Giorni feriali</option>
              <option value="weekly">Ogni settimana</option>
            </select>
          </div>
          {recurrence !== "none" && (
            <RecurrenceEndFields value={end} onChange={setEnd} testIdPrefix={`${testIdPrefix}-task`} minDate={defaultDate || date} />
          )}
        </div>
      )}
      {collapsible && (
        <button type="button" onClick={() => setOpen(false)} className="md:hidden mt-3 w-full text-xs text-[#6A7368] underline" data-testid={`${testIdPrefix}-task-collapse`}>
          Chiudi
        </button>
      )}
    </form>
    </>
  );
}
